import { Link } from 'react-router-dom'
import { Play, Trophy, Users, TrendingUp } from 'lucide-react'

function HomePage() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Govern Your Nation
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-primary-100 max-w-3xl mx-auto">
            Experience the challenges and rewards of leading a country through 
            economic crises, diplomatic negotiations, and political upheaval.
          </p>
          <div className="space-x-4">
            <Link to="/register" className="inline-flex items-center space-x-2 bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              <Play className="w-5 h-5" />
              <span>Start Playing</span>
            </Link>
            <Link to="/login" className="inline-flex items-center space-x-2 border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-colors">
              <span>Login</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Master the Art of Governance
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Navigate complex political, economic, and social challenges in this 
              comprehensive government simulation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Economic Management</h3>
              <p className="text-gray-600">
                Balance budgets, set tax rates, and manage public spending to keep 
                your economy thriving while maintaining public approval.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-success-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-success-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Diplomatic Relations</h3>
              <p className="text-gray-600">
                Build alliances, negotiate trade deals, and manage international 
                relations while protecting your national interests.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-warning-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trophy className="w-8 h-8 text-warning-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">AI-Powered Events</h3>
              <p className="text-gray-600">
                Experience dynamic world events generated by Cloudflare AI that adapt 
                to your decisions and create unique, personalized storylines.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Game Features */}
      <section className="bg-gray-100 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Rich Simulation Features
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <h4 className="font-semibold text-lg mb-2">Budget Management</h4>
              <p className="text-gray-600 text-sm">Allocate funds across education, healthcare, defense, and infrastructure</p>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-2">Tax Policy</h4>
              <p className="text-gray-600 text-sm">Set income, corporate, and sales tax rates with real economic effects</p>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-2">Technology Trees</h4>
              <p className="text-gray-600 text-sm">Research new technologies to unlock advanced capabilities</p>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-2">Trade Routes</h4>
              <p className="text-gray-600 text-sm">Establish international trade relationships and manage resources</p>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-2">Cabinet Management</h4>
              <p className="text-gray-600 text-sm">Appoint ministers and manage government departments</p>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-2">Event System</h4>
              <p className="text-gray-600 text-sm">Respond to dynamic events that affect your nation</p>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-2">Opposition Politics</h4>
              <p className="text-gray-600 text-sm">Navigate political challenges and maintain parliamentary support</p>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-2">Cloudflare AI Integration</h4>
              <p className="text-gray-600 text-sm">Powered by advanced AI models for dynamic events and intelligent gameplay</p>
            </div>
          </div>
        </div>
      </section>

      {/* AI Features Highlight */}
      <section className="bg-gradient-to-r from-purple-600 to-blue-600 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">🤖 Powered by AI</h2>
          <p className="text-xl text-purple-100 mb-8 max-w-3xl mx-auto">
            Experience the next generation of government simulation with Cloudflare AI. 
            Dynamic events, intelligent opposition, and personalized storytelling adapt to your unique governing style.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/ai-features" className="inline-flex items-center space-x-2 bg-white text-purple-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              <span>🧠</span>
              <span>Explore AI Features</span>
            </Link>
            <Link to="/register" className="inline-flex items-center space-x-2 border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-purple-600 transition-colors">
              <Play className="w-5 h-5" />
              <span>Start Playing</span>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary-600 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Lead?</h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Join thousands of players already experiencing the ultimate political simulation game.
          </p>
          <Link to="/register" className="inline-flex items-center space-x-2 bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
            <Play className="w-5 h-5" />
            <span>Start Your Campaign</span>
          </Link>
        </div>
      </section>
    </div>
  )
}

export default HomePage